import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import CancelIcon from "@mui/icons-material/Close";
import "./style.css";
import { useDispatch, useSelector } from "react-redux";
import ExcelIcon from "./../../../Assests/excel.svg";
import DownloadIcon from "./../../../Assests/download.svg";
import InfoIcon from "@mui/icons-material/Info";
import CancelIconRed from "./../../../Assests/cancel.svg";
import {
  Button,
  Grid,
  DialogActions,
  DialogContent,
  Box,
  Modal,
  CircularProgress,
  LinearProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import {
  addPackingPlantAddMinorIngredient,
  addProcessingPackagingPlantsDiff,
  addSourceBePartOfOptimization,
  handleTemplateLTDownLoadByType,
  handleExistingTemplateDownLoad,
} from "../../../Redux/Actions/InputData";
import React, { useEffect, useRef, useState } from "react";
import {
  API_BASE_URL,
  LONG_EXCEL_FILE_UPLOAD_API,
  LONG_TERM_AZURE_PATH_API,
  LONG_TERM_BASIC_VALIDATION_API,
} from "../../../Utils/Config";
import { getRequest } from "../../../Services/AxiosConfiguration";
import * as actions from "../../../Redux/Actions/index";
import axios from "axios";
import { ActivateOrDeactivateFolder } from "../../../Services/FolderService";
import JSZip from "jszip";
import { BlockBlobClient } from "@azure/storage-blob";

// ---------------------------------------------------------------------------
// Upload limits / constants
// ---------------------------------------------------------------------------
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".xlsx", ".xlsm", ".xls"];
// Give large uploads (validation + azure-path handshake) enough time instead
// of relying on axios' default of "no timeout", which hangs forever on a
// stalled connection with no user feedback.
const VALIDATION_TIMEOUT_MS = 5 * 60 * 1000; // 5 min
const AZURE_PATH_TIMEOUT_MS = 5 * 60 * 1000; // 5 min

const BootstrapDialog = styled(Dialog)(({ theme }: any) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

interface LongTermUploadContentProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFolderId: any;
  selectedScenarioId: any;
  handleGoBack: () => void;
  selectRadio: string;
  showCrossMark?: boolean;
  /** When true, renders body without wrapping Dialog (for embedding in another modal) */
  embedded?: boolean;
  /** When provided, used as authoritative module (e.g. "module6") - avoids Redux sync issues */
  moduleKey?: string;
}

// ---------------------------------------------------------------------------
// Client-side guardrails before we touch the network at all. Cheap checks
// like size/extension should fail instantly instead of burning a 50MB
// upload attempt on something we could have rejected locally.
// ---------------------------------------------------------------------------
const getFileSizeErrorMessage = (file: File): string | null => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    return `File is ${sizeMb} MB, which exceeds the ${MAX_FILE_SIZE_MB} MB limit. Please upload a smaller file.`;
  }
  if (file.size === 0) {
    return "The selected file is empty.";
  }
  return null;
};

const getExtensionErrorMessage = (file: File): string | null => {
  const name = file.name.toLowerCase();
  const isAllowed = ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
  return isAllowed
    ? null
    : `Unsupported file type. Please upload one of: ${ALLOWED_EXTENSIONS.join(", ")}`;
};

const validateFileLocally = (file: File): string[] => {
  const errors: string[] = [];
  const sizeErr = getFileSizeErrorMessage(file);
  const extErr = getExtensionErrorMessage(file);
  if (sizeErr) errors.push(sizeErr);
  if (extErr) errors.push(extErr);
  return errors;
};

// ---------------------------------------------------------------------------
// Step 1: call the backend's basic-validation endpoint before we touch the file.
// It returns whether the file structure is OK ("isValid") and whether it
// actually contains rows worth processing ("hasData").
// TODO: rename the fields below to match your real API response shape.
// ---------------------------------------------------------------------------
const validateExcelFile = async (
  file: File,
  params: {
    fileType?: string | number;
    runType: string;
    uploadType: string;
    folderId: any;
    scenarioId: any;
  },
  onUploadProgress?: (percent: number) => void,
): Promise<{ isValid: boolean; hasData: boolean; message?: string; errors?: string[] }> => {
  const formData = new FormData();

  formData.append("file", file);                    // IFormFile file
  if (params.fileType !== undefined && params.fileType !== null) {
    formData.append("fileType", String(params.fileType)); // string? fileType (optional)
  }
  formData.append("runType", params.runType);         // string runType
  formData.append("uploadType", params.uploadType);   // string uploadType
  formData.append("folderId", String(params.folderId));     // string folderId
  formData.append("scenarioId", String(params.scenarioId)); // string scenarioId

  try {
    const response = await axios.post(
      API_BASE_URL + LONG_TERM_BASIC_VALIDATION_API,
      formData,
      {
        timeout: VALIDATION_TIMEOUT_MS,
        onUploadProgress: (evt) => {
          if (onUploadProgress && evt.total) {
            onUploadProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
      },
    );
    const data = response.data ?? {};
    return {
      isValid: data.isValid ?? data.IsValid ?? false,
      hasData: data.hasData ?? data.HasData ?? false,
      message: data.message ?? data.Message,
      errors: data.errors ?? data.Errors ?? [],
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.code === "ECONNABORTED") {
        return {
          isValid: false,
          hasData: false,
          message: "Validation timed out. Please check your connection and try again.",
          errors: [],
        };
      }
      const errorData = error.response?.data;
      return {
        isValid: false,
        hasData: false,
        message: errorData?.message ?? "Validation failed",
        errors: errorData?.errors ?? [],
      };
    }

    return {
      isValid: false,
      hasData: false,
      message: "An unexpected error occurred.",
      errors: [],
    };
  }
};

// ---------------------------------------------------------------------------
// Step 2: read the uploaded workbook in the browser, append the 3 extra
// sheets, and hand back a new File object (so the rest of your code can
// keep treating it exactly like a normal uploaded file).
//
// PRODUCTION NOTE (root-caused against a real 40MB / ~397MB-uncompressed
// sample file): the "RangeError: Invalid array length" was NOT corrupt
// metadata. Inspecting the file directly showed sheets like
// PrimaryTransportation (859,429 rows) and Demand (233,119 rows) whose XML
// alone is ~297MB and ~69MB uncompressed. Loading that into SheetJS's
// in-memory cell-object model and then re-serializing the whole workbook to
// add 3 tiny sheets is what exhausts the browser's array/allocation limits
// - it has nothing to do with file corruption and won't be fixed by read
// flags. The only approach that scales here is to skip parsing the
// existing sheets entirely: an .xlsx is just a zip of XML parts, so we open
// it as a zip, inject 3 new small worksheet XML parts + the few lines of
// workbook.xml/rels/[Content_Types].xml bookkeeping they need, and leave
// every existing sheet's compressed bytes completely untouched. Requires
// `npm install jszip`.
// ---------------------------------------------------------------------------
const yieldToMainThread = () => new Promise((resolve) => setTimeout(resolve, 0));

const escapeXml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

// 0-indexed column number -> spreadsheet column letters ("A", "B", ... "AA").
const colLetter = (colIndex: number): string => {
  let letters = "";
  let n = colIndex + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
};

// Builds a minimal, valid worksheet XML part from an array of flat row
// objects (same shape you'd pass to XLSX.utils.json_to_sheet). Only plain
// values are supported - fine for the small control/metadata sheets we
// append here.
const buildSheetXml = (rows: Array<Record<string, string | number>>): string => {
  if (rows.length === 0) {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData/></worksheet>`;
  }

  const headers = Object.keys(rows[0]);
  const totalRows = rows.length + 1; // +1 for header row
  const dimension = `A1:${colLetter(headers.length - 1)}${totalRows}`;

  let rowsXml = "";

  let headerCells = "";
  headers.forEach((h, colIdx) => {
    headerCells += `<c r="${colLetter(colIdx)}1" t="inlineStr"><is><t>${escapeXml(h)}</t></is></c>`;
  });
  rowsXml += `<row r="1">${headerCells}</row>`;

  rows.forEach((row, rowIdx) => {
    const excelRow = rowIdx + 2;
    let cells = "";
    headers.forEach((h, colIdx) => {
      const ref = `${colLetter(colIdx)}${excelRow}`;
      const value = row[h];
      if (typeof value === "number" && Number.isFinite(value)) {
        cells += `<c r="${ref}"><v>${value}</v></c>`;
      } else {
        const strValue = value === null || value === undefined ? "" : String(value);
        cells += `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(strValue)}</t></is></c>`;
      }
    });
    rowsXml += `<row r="${excelRow}">${cells}</row>`;
  });

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><dimension ref="${dimension}"/><sheetData>${rowsXml}</sheetData></worksheet>`;
};

const addExtraSheetsToWorkbook = async (
  file: File,
  extraSheetValues: {
    closingProcessing: string | number;
    closingPacking: string | number;
    questionnaireAnswers: { ques1: string; ques2: string; ques3: string };
    constraints: Array<{
      constrainType: string;
      criteria1: string;
      criteria2: string;
      symbol: string;
      value: string | number;
      timePeriod: string;
    }>;
  },
): Promise<File> => {
  const buffer = await file.arrayBuffer();
  await yieldToMainThread();

  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(buffer);
  } catch (err: any) {
    err.message = `[zip:load] ${err.message}`;
    throw err;
  }

  const workbookXmlPath = "xl/workbook.xml";
  const workbookRelsPath = "xl/_rels/workbook.xml.rels";
  const contentTypesPath = "[Content_Types].xml";

  const workbookXmlFile = zip.file(workbookXmlPath);
  const workbookRelsFile = zip.file(workbookRelsPath);
  const contentTypesFile = zip.file(contentTypesPath);
  if (!workbookXmlFile || !workbookRelsFile || !contentTypesFile) {
    throw new Error(
      "[zip:parts] Uploaded file is not a valid .xlsx package (missing workbook.xml / rels / content-types).",
    );
  }

  // These 3 control files are always tiny (KBs), regardless of how huge the
  // sheets themselves are - safe to read fully into memory.
  const [workbookXml, workbookRelsXml, contentTypesXml] = await Promise.all([
    workbookXmlFile.async("string"),
    workbookRelsFile.async("string"),
    contentTypesFile.async("string"),
  ]);

  // Find the next free sheetN.xml index / r:id / sheetId so the new parts
  // never collide with existing ones.
  const existingSheetIndexes = Object.keys(zip.files)
    .map((p) => p.match(/^xl\/worksheets\/sheet(\d+)\.xml$/))
    .filter((m): m is RegExpMatchArray => m !== null)
    .map((m) => parseInt(m[1], 10));
  let nextIndex = (existingSheetIndexes.length ? Math.max(...existingSheetIndexes) : 0) + 1;

  const existingRIds = Array.from(workbookRelsXml.matchAll(/Id="rId(\d+)"/g)).map((m) =>
    parseInt(m[1], 10),
  );
  let nextRId = (existingRIds.length ? Math.max(...existingRIds) : 0) + 1;

  const existingSheetIds = Array.from(workbookXml.matchAll(/sheetId="(\d+)"/g)).map((m) =>
    parseInt(m[1], 10),
  );
  let nextSheetId = (existingSheetIds.length ? Math.max(...existingSheetIds) : 0) + 1;

  const sheetsToAdd: Array<{ name: string; rows: Array<Record<string, string | number>> }> = [
    {
      name: "noofclosing",
      rows: [
        {
          Closing_Processing: extraSheetValues.closingProcessing,
          Closing_Packing: extraSheetValues.closingPacking,
        },
      ],
    },
    {
      name: "questionaire",
      rows: [
        {
          Ques_1: extraSheetValues.questionnaireAnswers.ques1,
          Ques_2: extraSheetValues.questionnaireAnswers.ques2,
          Ques_3: extraSheetValues.questionnaireAnswers.ques3,
        },
      ],
    },
    {
      name: "dynamic_constrain_data",
      rows: extraSheetValues.constraints.map((c) => ({
        constrain_type: c.constrainType,
        Criteria_1: c.criteria1,
        Criteria_2: c.criteria2,
        Symbol: c.symbol,
        Value: c.value,
        "Time period": c.timePeriod,
      })),
    },
  ];

  let newSheetEntriesXml = "";
  let newRelsXml = "";
  let newContentTypesXml = "";

  try {
    sheetsToAdd.forEach((sheet) => {
      const fileName = `sheet${nextIndex}.xml`;
      const rId = `rId${nextRId}`;
      zip.file(`xl/worksheets/${fileName}`, buildSheetXml(sheet.rows));

      newSheetEntriesXml += `<sheet name="${escapeXml(sheet.name)}" sheetId="${nextSheetId}" r:id="${rId}"/>`;
      newRelsXml += `<Relationship Id="${rId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/${fileName}"/>`;
      newContentTypesXml += `<Override PartName="/xl/worksheets/${fileName}" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`;

      nextIndex += 1;
      nextRId += 1;
      nextSheetId += 1;
    });
  } catch (err: any) {
    err.message = `[zip:build-sheets] ${err.message}`;
    throw err;
  }

  if (!workbookXml.includes("</sheets>") || !workbookRelsXml.includes("</Relationships>") || !contentTypesXml.includes("</Types>")) {
    throw new Error("[zip:bookkeeping] Unexpected workbook.xml/rels/content-types structure - could not append new sheets.");
  }

  zip.file(workbookXmlPath, workbookXml.replace("</sheets>", `${newSheetEntriesXml}</sheets>`));
  zip.file(workbookRelsPath, workbookRelsXml.replace("</Relationships>", `${newRelsXml}</Relationships>`));
  zip.file(contentTypesPath, contentTypesXml.replace("</Types>", `${newContentTypesXml}</Types>`));

  await yieldToMainThread();

  // Only the handful of parts we touched (workbook.xml, rels,
  // content-types, and the 3 new tiny sheet parts) get (re)compressed here.
  // JSZip reuses the original compressed bytes for every untouched entry,
  // so the existing 300MB+ sheets are streamed straight through without
  // ever being inflated.
  let outBlob: Blob;
  try {
    outBlob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 6 },
    });
  } catch (err: any) {
    err.message = `[zip:generate] ${err.message}`;
    throw err;
  }

  return new File([outBlob], file.name, { type: file.type });
};

// ---------------------------------------------------------------------------
// Step 3: send the enriched file to [HttpPost("getazurepath")] so the backend
// can upload it to Blob Storage and hand back the path/URL.
// TODO: adjust the field names to match what your API expects/returns.
// ---------------------------------------------------------------------------
const getAzurePath = async (
  folderId: any,
  scenarioId: any,
  runtype: string,
  usecase: string,
  category: string,
  fileType: string | number,
): Promise<any> => {
  const response = await axios.post(
    API_BASE_URL + LONG_TERM_AZURE_PATH_API,
    null,
    {
      timeout: AZURE_PATH_TIMEOUT_MS,
      params: {
        folderId,
        scenarioId,
        runtype,
        usecase,
        category,
        fileType: String(fileType),
      },
    },
  );

  return response.data;
};

// ---------------------------------------------------------------------------
// Step 4: upload the enriched file directly to Azure Blob Storage using a
// SAS URL. Tuned block size / concurrency for files up to ~50MB, with a
// progress callback so the UI isn't silent for 10-30+ seconds.
// ---------------------------------------------------------------------------
const uploadToAzureBlob = async (
  sasUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  const blockBlobClient = new BlockBlobClient(sasUrl);
  await blockBlobClient.uploadBrowserData(file, {
    blockSize: 8 * 1024 * 1024, // 8MB blocks - fewer round trips for 50MB files
    concurrency: 4, // parallel block uploads
    maxSingleShotSize: 8 * 1024 * 1024,
    blobHTTPHeaders: {
      blobContentType:
        file.type ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
    onProgress: (ev) => {
      if (onProgress && file.size > 0) {
        onProgress(Math.round((ev.loadedBytes * 100) / file.size));
      }
    },
  });
};

// ---------------------------------------------------------------------------
// Step 5 (module4 direct-to-Azure flow): tell the backend the blob upload
// finished, via [Route("excelupload/longterm/getuploaddetails")].

// NOTE: this call only tells the backend "the file is on blob storage now,
// go ahead and kick off the run". It intentionally returns nothing the UI
// needs to render - the actual per-file / per-stage status (the table shown
// in the "Files Uploaded Successfully" modal, e.g. "Scenario - Processing",
// "Gurobi - Waiting") comes from the SAME polling mechanism every other
// module uses (handleFileUploadApi / handleOpenApicall). So after this call
// succeeds we must still kick off that poll - see handleUploadNow below.
// ---------------------------------------------------------------------------
interface LongTermUploadTableModel {
  folder_id: number;
  scenario_id: number;
  module_id: number;
}

const notifyExcelUploadDetails = async (
  uploadTableModel: LongTermUploadTableModel,
): Promise<any> => {
  const response = await axios.post(
    API_BASE_URL + LONG_EXCEL_FILE_UPLOAD_API,
    uploadTableModel,
    { timeout: AZURE_PATH_TIMEOUT_MS },
  );
  return response.data;
};

function LongTermUploadContent(props: LongTermUploadContentProps) {
  const { moduleKey: moduleKeyProp } = props;
  const selectedUsecaseId = useSelector(
    (state: any) => state.InputDataReducers.selectedUsecaseId,
  );
  const isDone = useSelector(
    (state: any) => state.InputDataReducers.isDone,
  );
  const isLoading = useSelector(
    (state: any) => state.InputDataReducers.isLoading,
  );
  const selectedFolderId = useSelector(
    (state: any) => state.InputDataReducers.selectedFolderId,
  );
  const selectedScenarioId = useSelector(
    (state: any) => state.InputDataReducers.selectedScenarioId,
  );
  const userData = useSelector(
    (state: any) => state.InputDataReducers.apiUserData,
  );

  const dispatch: any = useDispatch();

  const getDataArr: any = [
    { id: 1, name: "Upload Module 1 File", btnName: "upload", selectedFile: {}, ref: React.useRef(null) },
  ];
  const getDataModule2: any = [
    { id: 1, name: "Upload Module 2 File", btnName: "upload", selectedFile: {}, ref: React.useRef(null) },
  ];
  const getDataModule3: any = [
    { id: 1, name: "Upload Module 3 File", btnName: "upload", selectedFile: {}, ref: React.useRef(null) },
  ];
  const getDataModule4: any = [
    { id: 1, name: "Upload Module 4 File", btnName: "upload", selectedFile: {}, ref: React.useRef(null) },
  ];
  const getDataModule5: any = [
    { id: 1, name: "Upload Module 5 File", btnName: "upload", selectedFile: {}, ref: React.useRef(null) },
  ];
  const getDataModule6: any = [
    { id: 1, name: "Upload Module 6 File", btnName: "upload", selectedFile: {}, ref: React.useRef(null) },
  ];

  const [selectedfileList, setSelectedFileList] = useState<any>([]);
  const [selectedfilesList, setSelectedFilesList] = useState<any>([]);
  const [getMasterData, setGetMasterData] = useState<any>([]);
  const [getData, setMasterData] = useState<any>([]);
  const [ModuleForUpload, setModuleForUpload] = useState<any>("");
  const [isLongTermPopup, SetIsLongTermPopup] = useState<any>(false);
  const [selectedfile, setSelectedFile] = useState<any>({});
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  // --- New state for the validate -> enrich -> get-azure-path flow ---
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState<string>("");
  const [validationUploadPct, setValidationUploadPct] = useState<number>(0);
  const [blobUploadPct, setBlobUploadPct] = useState<number>(0);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationErrorDialogOpen, setValidationErrorDialogOpen] = useState(false);

  const handleTemplateDownLoad = async () => {
    const fileModule = ModuleForUpload;
    if (!fileModule || isDownloadingTemplate) return;
    setIsDownloadingTemplate(true);
    try {
      await handleTemplateLTDownLoadByType(fileModule);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleRerunTemplateDownLoad = async () => {
    if (isDownloadingTemplate) return;
    setIsDownloadingTemplate(true);
    try {
      await handleExistingTemplateDownLoad(selectedFolderId, selectedScenarioId);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleChangeFileUpload = (e: any) => {
    const fileUploaded = e.target.files[0];
    // Always reset the input value so re-selecting the same file re-fires onChange.
    e.target.value = "";
    if (!fileUploaded) {
      return;
    }

    // ---- Client-side guardrails before we accept the file into state ----
    const localErrors = validateFileLocally(fileUploaded);
    if (localErrors.length > 0) {
      setValidationErrors(localErrors);
      setValidationErrorDialogOpen(true);
      return;
    }

    selectedfileList.length = 0;
    selectedfileList.push(fileUploaded.name);
    selectedfilesList.push({
      filename: fileUploaded.name,
      filedata: fileUploaded,
    });
    const newArr = getMasterData.map((ele: any) => {
      if (selectedItemId === ele.id) {
        ele.selectedFile = fileUploaded;
      }
      return ele;
    });
    setGetMasterData(newArr);
    setSelectedFile(fileUploaded);
  };
  const [selectedItemId, setSelectedItemId] = useState<any>("");

  const getIsProcessingPackagingPlantsDiff = useSelector(
    (state: any) => state.InputDataReducers.isProcessingPackagingPlantsDiff,
  );
  const getIsSourceBePartOfOptimization = useSelector(
    (state: any) => state.InputDataReducers.isSourceBePartOfOptimization,
  );
  const getIsPackingPlantAddMinorIngredient = useSelector(
    (state: any) => state.InputDataReducers.isPackingPlantAddMinorIngredient,
  );
  const selectedFolderData = useSelector(
    (state: any) => state.InputDataReducers.selectedFolderData,
  );

  let folderModuleName = selectedFolderData?.folder?.slice(0, 7);

  const setModules = () => {
    const normalizedFolderModule = folderModuleName?.toLowerCase?.();
    const moduleFromFolder =
      normalizedFolderModule && /^module[1-6]$/.test(normalizedFolderModule)
        ? normalizedFolderModule
        : null;
    const resolvedModule =
      moduleKeyProp && /^module[1-6]$/.test(moduleKeyProp)
        ? moduleKeyProp
        : moduleFromFolder;

    if (resolvedModule) {
      const moduleDataMap: Record<string, any> = {
        module1: getDataArr,
        module2: getDataModule2,
        module3: getDataModule3,
        module4: getDataModule4,
        module5: getDataModule5,
        module6: getDataModule6,
      };
      const data = moduleDataMap[resolvedModule];
      if (data) {
        setMasterData(data);
        setGetMasterData(data);
        setModuleForUpload(resolvedModule);
        return;
      }
    }

    if (getIsProcessingPackagingPlantsDiff && getIsSourceBePartOfOptimization && getIsPackingPlantAddMinorIngredient) {
      setMasterData(getDataArr);
      setGetMasterData(getDataArr);
      setModuleForUpload("module1");
    } else if (getIsProcessingPackagingPlantsDiff && !getIsSourceBePartOfOptimization && getIsPackingPlantAddMinorIngredient) {
      setMasterData(getDataModule2);
      setGetMasterData(getDataModule2);
      setModuleForUpload("module2");
    } else if (!getIsProcessingPackagingPlantsDiff && getIsSourceBePartOfOptimization && !getIsPackingPlantAddMinorIngredient) {
      setMasterData(getDataModule3);
      setGetMasterData(getDataModule3);
      setModuleForUpload("module3");
    } else if (!getIsProcessingPackagingPlantsDiff && !getIsSourceBePartOfOptimization && !getIsPackingPlantAddMinorIngredient) {
      setMasterData(getDataModule4);
      setGetMasterData(getDataModule4);
      setModuleForUpload("module4");
    } else if (getIsProcessingPackagingPlantsDiff && getIsSourceBePartOfOptimization && !getIsPackingPlantAddMinorIngredient) {
      setMasterData(getDataModule5);
      setGetMasterData(getDataModule5);
      setModuleForUpload("module5");
    } else if (getIsProcessingPackagingPlantsDiff && !getIsSourceBePartOfOptimization && !getIsPackingPlantAddMinorIngredient) {
      setMasterData(getDataModule6);
      setGetMasterData(getDataModule6);
      setModuleForUpload("module6");
    }
  };

  useEffect(() => {
    setModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    moduleKeyProp,
    folderModuleName,
    getIsProcessingPackagingPlantsDiff,
    getIsSourceBePartOfOptimization,
    getIsPackingPlantAddMinorIngredient,
  ]);

  const handleUploadFile = (e: any, item: any, index: number) => {
    e.preventDefault();
    getData[index]?.ref?.current?.click();
    setSelectedItemId(item.id);
  };

  const handleCancelFile = (e: any, elem: any) => {
    const newArr = getMasterData?.filter((ele: any) => {
      if (ele.id === elem.id) {
        ele.selectedFile = {};
      }
      return ele;
    });
    setGetMasterData(newArr);
  };

  const [rerunUploadStatusData, setRerunUploadStatusData] = useState<any>([]);
  const [longTermExcelStatus, setLongTermExcelStatus] = useState<any>(false);
  const [intervalId, setIntervalId] = useState<any>(null);
  const [openReRunDialogStatus, setOpenReRunDialogStatus] = useState<any>("");
  const [progressBarStatus, setProgressBarStatus] = useState<any>(false);
  const [failedStatus, setFailedStatus] = useState<any>(false);
  const [successStatus, setSuccessStatus] = useState<any>(false);
  const uploadInitiatedRef = useRef(false);
  // Poll counters live in a ref instead of module-level `let`s so concurrent
  // polls / re-renders don't clobber each other's counts.
  const pollCountsRef = useRef({ failed: 0, success: 0 });

  useEffect(() => {
    if (isDone && uploadInitiatedRef.current) {
      uploadInitiatedRef.current = false;
      handleReRunDialog(true);
    }
  }, [isDone]);

  // Make sure a stray polling interval never survives an unmount (e.g. user
  // navigates away mid-run) - previously this leaked and kept firing.
  useEffect(() => {
    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  const clearUploadModuleStore = () => {
    dispatch(addProcessingPackagingPlantsDiff(true));
    dispatch(addSourceBePartOfOptimization(true));
    dispatch(addPackingPlantAddMinorIngredient(true));
  };

  const handleOpenApicall = (urls: any, requestParam: any) => {
    axios({
      url: urls,
      method: "post",
      headers: { "Content-Type": "application/json;" },
      data: requestParam,
    }).then((response) => {
      if (response.status === 200) {
        setRerunUploadStatusData(response.data);
        setProgressBarStatus(true);
        const rerunData = response.data;
        rerunData?.forEach((item: any) => {
          if (
            item?.status.toLowerCase().includes("failed") ||
            item?.status.toLowerCase().includes("fail")
          ) {
            setOpenReRunDialogStatus(item?.errorMessage);
            pollCountsRef.current.failed += 1;
          } else if (item?.status.toLowerCase().includes("completed")) {
            setOpenReRunDialogStatus(item?.status);
            pollCountsRef.current.success += 1;
          }
        });

        if (response.data[1]?.status === "Module Run Successfully Completed") {
          setSuccessStatus(true);
          setProgressBarStatus(false);
          if (intervalId !== null) clearInterval(intervalId);
        }
        if (pollCountsRef.current.failed > 0) {
          setProgressBarStatus(false);
          ActivateOrDeactivateFolder(selectedUsecaseId, selectedFolderId, 0);
          setFailedStatus(true);
          if (intervalId !== null) clearInterval(intervalId);
        }
        if (response?.data?.length === pollCountsRef.current.success) {
          setProgressBarStatus(false);
          if (intervalId !== null) clearInterval(intervalId);
        }
      }
    });
  };

  // The ONLY place that calls LONG_EXCEL_FILE_UPLOAD_API ("uploadltFile") to
  // start the run-status poll. Invoked from handleReRunDialog for every
  // module - including module4, whose file already sits in Blob Storage by
  // the time this fires. The backend endpoint is shared: for modules 1/2/3/
  // 5/6 it also receives the raw file bytes upstream (via
  // onInitUloadMasterFile); for module4 the file was already handed off via
  // notifyExcelUploadDetails, so this call here is purely "start polling
  // run status", using the same folder/scenario/module identifiers.
  const handleFileUploadApi = () => {
    const folderNumber = ModuleForUpload.toLowerCase().replace("module", "");
    const urls = API_BASE_URL + LONG_EXCEL_FILE_UPLOAD_API;
    const requestParam = {
      folder_Id: Number(props?.selectedFolderId),
      scenario_Id: Number(props?.selectedScenarioId),
      module_Id: Number(folderNumber),
    };

    pollCountsRef.current = { failed: 0, success: 0 };
    // Fire immediately so the table populates right away instead of waiting
    // a full 20s for the first row to appear.
    handleOpenApicall(urls, requestParam);
    const apicallInterval = setInterval(() => {
      handleOpenApicall(urls, requestParam);
    }, 20000);
    setIntervalId(apicallInterval);
  };

  const handleCloseExcelPopup = () => {
    setLongTermExcelStatus(false);
    localStorage.setItem("selectCGOInput", "LongTermInput");
    localStorage.setItem("selectedUsecase", "Long Term Analysis");
    window.location.href = "/LongTermHome";
    setFailedStatus(false);
    setSuccessStatus(false);
  };

  // Opens the "Files Uploaded Successfully" status modal and always starts
  // the run-status poll - every module (including module4's direct-to-Azure
  // flow) needs live status rows (e.g. "Scenario - Processing",
  // "Gurobi - Waiting") rather than a single static message, since the
  // backend keeps working after the file lands.
  const handleReRunDialog = (done: boolean) => {
    SetIsLongTermPopup(false);
    setLongTermExcelStatus(done);
    handleFileUploadApi();
  };

  const handleUploadNow = async (e: any, runType: string) => {
    e.preventDefault();

    const fileModule = ModuleForUpload;
    const fileType =
      folderModuleName === "module1" ? 5
      : folderModuleName === "module2" ? 9
      : folderModuleName === "module3" ? 11
      : folderModuleName === "module4" ? 13
      : folderModuleName === "module5" ? 15
      : 17;

    let MKey;
    if (getIsProcessingPackagingPlantsDiff && getIsSourceBePartOfOptimization && getIsPackingPlantAddMinorIngredient) {
      setModuleForUpload("module1");
      MKey = 1;
    } else if (getIsProcessingPackagingPlantsDiff && !getIsSourceBePartOfOptimization && getIsPackingPlantAddMinorIngredient) {
      setModuleForUpload("module2");
      MKey = 2;
    } else if (!getIsProcessingPackagingPlantsDiff && getIsSourceBePartOfOptimization && !getIsPackingPlantAddMinorIngredient) {
      setModuleForUpload("module3");
      MKey = 3;
    } else if (!getIsProcessingPackagingPlantsDiff && !getIsSourceBePartOfOptimization && !getIsPackingPlantAddMinorIngredient) {
      setModuleForUpload("module4");
      MKey = 4;
    } else if (getIsProcessingPackagingPlantsDiff && getIsSourceBePartOfOptimization && !getIsPackingPlantAddMinorIngredient) {
      setModuleForUpload("module5");
      MKey = 5;
    } else if (getIsProcessingPackagingPlantsDiff && !getIsSourceBePartOfOptimization && !getIsPackingPlantAddMinorIngredient) {
      setModuleForUpload("module6");
      MKey = 6;
    }

    // resolvedModule mirrors the logic in setModules(): prefer the module
    // that was explicitly resolved (moduleKeyProp / folder name), falling
    // back to the MKey computed above from the boolean flags.
    const resolvedModule = ModuleForUpload || `module${MKey}`;
    const isModule4 = resolvedModule === "module4";

    const initialValues = {
      folder: props?.selectedFolderId,
      scenario: props?.selectedScenarioId,
      folderModuleName_id: Number(folderModuleName?.replace("module", "") ?? MKey),
    };

    if (isModule4) {
      const fileToProcess = getMasterData.find(
        (itm: any) => itm.selectedFile && itm.selectedFile.name,
      )?.selectedFile;

      if (!fileToProcess) {
        setValidationErrors(["Please select a file before uploading."]);
        setValidationErrorDialogOpen(true);
        return;
      }

      // ---- Re-validate size/type here too, in case selectedFile was set
      // through a path that skipped handleChangeFileUpload's checks. ----
      const localErrors = validateFileLocally(fileToProcess);
      if (localErrors.length > 0) {
        setValidationErrors(localErrors);
        setValidationErrorDialogOpen(true);
        return;
      }

      setValidationErrors([]);
      setIsProcessing(true);
      setValidationUploadPct(0);
      setBlobUploadPct(0);

      // Track the stage in a plain variable (not just state) - setState
      // inside this closure won't be readable synchronously later in this
      // same function call, so relying on the `processingStage` state
      // variable in the catch block below would report a stale/previous
      // stage instead of the one that actually failed.
      let currentStage = "";
      const setStage = (stage: string) => {
        currentStage = stage;
        setProcessingStage(stage);
      };

      try {
        console.log("[LongTermUpload] file:", {
          name: fileToProcess.name,
          size: fileToProcess.size,
          type: fileToProcess.type,
        });

        // ---- Step 1: validate ----
        setStage("Validating file...");
        const validationResult = await validateExcelFile(
          fileToProcess,
          {
            fileType,
            runType,
            uploadType: "longTerm", // TODO: confirm this matches what your backend expects
            folderId: props?.selectedFolderId,
            scenarioId: props?.selectedScenarioId,
          },
          setValidationUploadPct,
        );

        if (!validationResult.isValid) {
          const errors = validationResult.errors?.length
            ? validationResult.errors
            : [validationResult.message || "File failed validation."];
          setValidationErrors(errors);
          setValidationErrorDialogOpen(true);
          return;
        }
        if (!validationResult.hasData) {
          setValidationErrors(["No data found in the uploaded file."]);
          setValidationErrorDialogOpen(true);
          return;
        }

        // ---- Step 2: add the 3 extra sheets ----
        // TODO: replace these placeholder values with the real data from
        // wherever your app collects them (state/selectors/forms).
        setStage("Preparing workbook...");
        const enrichedFile = await addExtraSheetsToWorkbook(fileToProcess, {
          closingProcessing: "0",
          closingPacking: "0",
          questionnaireAnswers: { ques1: "N0", ques2: "No", ques3: "No" },
          constraints: [
            { constrainType: "", criteria1: "", criteria2: "", symbol: "", value: "", timePeriod: "" },
          ],
        });

        // ---- Step 3: get the azure path for the enriched file ----
        setStage("Requesting upload location...");
        const azureResult = await getAzurePath(
          props?.selectedFolderId,
          props?.selectedScenarioId,
          runType,
          "longTerm",
          fileModule,
          fileModule,
        );

        const uploadUrl: string = azureResult;
        const blobPath: string = uploadUrl?.split("?")[0];

        // ---- Step 4: upload directly to Azure Blob Storage (browser -> Azure) ----
        setStage("Uploading file...");
        await uploadToAzureBlob(uploadUrl, enrichedFile, setBlobUploadPct);
        console.log("Azure upload completed:", blobPath);
        // If uploadToAzureBlob throws, we jump straight to `catch` below -
        // nothing past this line runs.

        // ---- Step 5: notify the backend the upload is done (metadata only) ----
        setStage("Finalizing...");
        // resolvedModule is a string like "module4" - the backend model
        // wants the bare numeric id (4), same as folderModuleName_id above.
        const moduleIdNumber = Number(resolvedModule.replace("module", ""));
        await notifyExcelUploadDetails({
          folder_id: Number(props?.selectedFolderId),
          scenario_id: Number(props?.selectedScenarioId),
          module_id: moduleIdNumber,
        });

        setSelectedFilesList([]);

        // ---- Step 6: open the status modal and start polling run status ----
        // The blob is uploaded and the backend has been told about it, but
        // the actual run (Scenario processing / Gurobi solve, etc.) still
        // happens server-side. handleReRunDialog opens the "Files Uploaded
        // Successfully" modal AND kicks off the same run-status poll every
        // other module uses, so the table fills in with live rows instead
        // of staying empty.
        handleReRunDialog(true);
      } catch (err: any) {
        // Log the full error (including stack) so the browser console shows
        // exactly which line threw - "Invalid array length" on its own
        // doesn't say whether it came from xlsx, the Azure SDK, or elsewhere.
        console.error(`[LongTermUpload] Failed at stage "${currentStage}":`, err);

        const baseMessage =
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong while processing the file.";
        const stageLabel = currentStage ? ` (stage: ${currentStage})` : "";
        setValidationErrors([`${baseMessage}${stageLabel}`]);
        setValidationErrorDialogOpen(true);
      } finally {
        setIsProcessing(false);
        setProcessingStage("");
      }
      return;
    }

    // ---------------------------------------------------------------
    // Branch B: every other module -> original behavior, unchanged
    // ---------------------------------------------------------------
    const formData = new FormData();
    getMasterData.forEach((itm: any) => {
      if (itm.selectedFile && itm.selectedFile.name) {
        formData.append("files", itm.selectedFile);
      }
    });

    dispatch(
      actions.onInitUloadMasterFile(
        formData,
        selectedfilesList,
        fileType,
        initialValues,
        "type",
        fileModule,
        "longTerm",
        runType,
        userData.userId,
      ),
    );
    setSelectedFilesList([]);
    uploadInitiatedRef.current = true;
  };

  const body = (
    <>
      <div style={{ paddingTop: "24px", paddingLeft: "20px" }}>
        <Grid container spacing={1}>
          <Grid item xs={10} md={10} lg={10}>
            <h4>Upload the Long Term file</h4>
          </Grid>
          {props.showCrossMark && (
            <Grid item xs={2} md={2} lg={2}>
              <CancelIcon
                className="cancel-icon"
                onClick={() => {
                  props.onClose();
                  clearUploadModuleStore();
                }}
              />
            </Grid>
          )}
        </Grid>
      </div>
      {props.selectRadio === "masterData" ? (
        <DialogContent className="modal-body">
          <div className="upload-file-button">
            {getMasterData &&
              getMasterData?.map((ele: any, index: any) => (
                <Grid container spacing={1} key={index} className="uploadFile">
                  <Grid item xs={7} md={7} lg={7}>
                    <span> {ele.name}</span>
                  </Grid>
                  <Grid item xs={5} md={5} lg={5}>
                    {ele.selectedFile && ele.selectedFile?.name?.length > 0 ? (
                      <div className="seletcedFilename">
                        <img src={ExcelIcon} alt="" />{" "}
                        <span>{ele.selectedFile?.name}</span>{" "}
                        <span className="cancel-file" onClick={(e) => handleCancelFile(e, ele)}>
                          <img src={CancelIconRed} alt="" />
                        </span>
                      </div>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          className="uploadBtn"
                          onClick={(e) => handleUploadFile(e, ele, index)}
                        >
                          <img src={ExcelIcon} alt="ExcelIcon" />
                          {ele.btnName}
                        </Button>
                        <input
                          type="file"
                          id={"prefix_" + index}
                          ref={ele.ref}
                          style={{ display: "none" }}
                          accept={ALLOWED_EXTENSIONS.join(",")}
                          onChange={(e) => handleChangeFileUpload(e)}
                        />
                      </>
                    )}
                  </Grid>
                </Grid>
              ))}
          </div>

          <div
            className="download-templet"
            onClick={isDownloadingTemplate ? undefined : handleTemplateDownLoad}
            style={{
              cursor: isDownloadingTemplate ? "not-allowed" : "pointer",
              opacity: isDownloadingTemplate ? 0.8 : 1,
            }}
          >
            <a>
              {isDownloadingTemplate ? (
                <CircularProgress size={18} sx={{ mr: 1 }} />
              ) : (
                <img src={ExcelIcon} alt="" />
              )}{" "}
              <span> Download template </span>{" "}
              {!isDownloadingTemplate && <img src={DownloadIcon} alt="" />}
            </a>
            <p>Download the file with multiple sheets.</p>
          </div>

          <p style={{ fontSize: "12px", color: "#666", marginTop: 8 }}>
            Max file size: {MAX_FILE_SIZE_MB} MB. Supported formats: {ALLOWED_EXTENSIONS.join(", ")}.
          </p>

          {isProcessing && (
            <Box sx={{ mt: 2 }}>
              <p style={{ fontSize: "13px", marginBottom: 4 }}>{processingStage}</p>
              {processingStage === "Validating file..." && (
                <LinearProgress variant="determinate" value={validationUploadPct} />
              )}
              {processingStage === "Uploading file..." && (
                <LinearProgress variant="determinate" value={blobUploadPct} />
              )}
              {processingStage !== "Validating file..." &&
                processingStage !== "Uploading file..." && <LinearProgress />}
            </Box>
          )}

          <DialogActions>
            <div className="modal-action">
              <Button
                variant="contained"
                className="Next"
                onClick={(e) => handleUploadNow(e, "newrun")}
                disabled={isLoading || isProcessing}
                startIcon={
                  isLoading || isProcessing ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              >
                {isLoading || isProcessing ? "Uploading..." : "Upload Now"}
              </Button>
            </div>
          </DialogActions>
        </DialogContent>
      ) : (
        <DialogContent className="modal-body">
          <div className="upload-file-button">
            {getMasterData &&
              getMasterData?.map((ele: any, index: any) => (
                <Grid container spacing={1} key={index} className="uploadFile">
                  <Grid item xs={7} md={7} lg={7}>
                    <span>
                      {index + 1}
                      {". "}
                    </span>
                    <span> {ele.name}</span>
                  </Grid>
                  <Grid item xs={5} md={5} lg={5}>
                    {ele.selectedFile && ele.selectedFile?.name?.length > 0 ? (
                      <div className="seletcedFilename">
                        <img src={ExcelIcon} alt="" />{" "}
                        <span>{ele.selectedFile?.name}</span>{" "}
                        <span className="cancel-file" onClick={(e) => handleCancelFile(e, ele)}>
                          <img src={CancelIconRed} alt="" />
                        </span>
                      </div>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          className="uploadBtn"
                          onClick={(e) => handleUploadFile(e, ele, index)}
                        >
                          <img src={ExcelIcon} alt="ExcelIcon" />
                          {ele.btnName}
                        </Button>
                        <input
                          type="file"
                          id={"prefix_" + index}
                          ref={ele.ref}
                          style={{ display: "none" }}
                          accept={ALLOWED_EXTENSIONS.join(",")}
                          onChange={(e) => handleChangeFileUpload(e)}
                        />
                      </>
                    )}
                  </Grid>
                </Grid>
              ))}
          </div>

          <div
            className="download-templet"
            onClick={isDownloadingTemplate ? undefined : handleRerunTemplateDownLoad}
            style={{
              cursor: isDownloadingTemplate ? "not-allowed" : "pointer",
              opacity: isDownloadingTemplate ? 0.8 : 1,
            }}
          >
            <a>
              {isDownloadingTemplate ? (
                <CircularProgress size={18} sx={{ mr: 1 }} />
              ) : (
                <img src={ExcelIcon} alt="" />
              )}{" "}
              <span> Download template </span>{" "}
              {!isDownloadingTemplate && <img src={DownloadIcon} alt="" />}
            </a>
            <p>Download the file with multiple sheets and data.</p>
          </div>

          <p style={{ fontSize: "12px", color: "#666", marginTop: 8 }}>
            Max file size: {MAX_FILE_SIZE_MB} MB. Supported formats: {ALLOWED_EXTENSIONS.join(", ")}.
          </p>

          {isProcessing && (
            <Box sx={{ mt: 2 }}>
              <p style={{ fontSize: "13px", marginBottom: 4 }}>{processingStage}</p>
              {processingStage === "Validating file..." && (
                <LinearProgress variant="determinate" value={validationUploadPct} />
              )}
              {processingStage === "Uploading file..." && (
                <LinearProgress variant="determinate" value={blobUploadPct} />
              )}
              {processingStage !== "Validating file..." &&
                processingStage !== "Uploading file..." && <LinearProgress />}
            </Box>
          )}

          <DialogActions>
            <div className="modal-action">
              <Button
                variant="contained"
                className="Next"
                onClick={(e) => handleUploadNow(e, "rerun")}
                disabled={isLoading || isProcessing}
                startIcon={
                  isLoading || isProcessing ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              >
                {isLoading || isProcessing ? "Uploading..." : "Upload Now"}
              </Button>
            </div>
          </DialogActions>
        </DialogContent>
      )}
    </>
  );

  return (
    <div>
      {longTermExcelStatus && (
        <Modal
          open={longTermExcelStatus}
          onClose={() => {}}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          className="done-modal"
        >
          <DialogContent data-keyboard="false" data-backdrop="static">
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 600,
                bgcolor: "background.paper",
                border: "2px solid #000",
                boxShadow: 24,
                p: 4,
                height: 400,
                overflowY: "auto",
              }}
            >
              <Box>
                <p>
                  {(failedStatus || successStatus) && (
                    <CancelIcon className="cancel-icon" onClick={handleCloseExcelPopup} />
                  )}
                </p>
                {progressBarStatus && (
                  <Box>
                    <div style={{ textAlign: "center" }}>
                      <CircularProgress />
                    </div>
                  </Box>
                )}

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>File Name</TableCell>
                        <TableCell>File Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rerunUploadStatusData?.map((item: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>{item.fileName}</TableCell>
                          <TableCell>{item.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              {openReRunDialogStatus?.toLowerCase()?.includes("successfully") ? (
                <>
                  <h6>{openReRunDialogStatus}</h6>
                  <br />
                </>
              ) : !openReRunDialogStatus?.toLowerCase()?.includes("failed") ? (
                <>
                  <h6 className="error-modal-message">{openReRunDialogStatus}</h6>
                  <br />
                </>
              ) : (
                <></>
              )}
            </Box>
          </DialogContent>
        </Modal>
      )}

      {isLongTermPopup && (
        <Modal
          open={isLongTermPopup}
          onClose={() => {}}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
            }}
          >
            <h2 style={{ alignItems: "center", textAlign: "center" }} id="modal-title">
              Files Uploaded Successfully!
            </h2>
            <div style={{ alignItems: "center", textAlign: "center" }}>
              <Button onClick={() => handleReRunDialog(true)}>Close</Button>
            </div>
          </Box>
        </Modal>
      )}

      {/* Validation Error Dialog */}
      <Dialog
        open={validationErrorDialogOpen}
        onClose={() => setValidationErrorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "#0f4778",
            color: "#fff",
            height: "48px",
            display: "flex",
            alignItems: "center",
            paddingLeft: "16px",
          }}
        >
          <InfoIcon sx={{ color: "#ff3b30", fontSize: "18px", marginRight: "12px" }} />
          <span style={{ fontSize: "18px", fontWeight: 600 }}>ERROR</span>
        </Box>

        <DialogContent sx={{ padding: "24px", paddingBottom: "12px" }}>
          <ol
            style={{
              margin: 0,
              paddingLeft: "28px",
              fontSize: "16px",
              lineHeight: "1.8",
              color: "#333",
            }}
          >
            {validationErrors
              .filter((error) => !error.toLowerCase().startsWith("expected sheets:"))
              .map((error, index) => (
                <li key={index}>{error}</li>
              ))}
          </ol>

          {validationErrors
            .filter((error) => error.toLowerCase().startsWith("expected sheets:"))
            .map((error, index) => (
              <p
                key={index}
                style={{
                  marginTop: "32px",
                  marginBottom: 0,
                  fontSize: "16px",
                  lineHeight: "1.5",
                  color: "#333",
                }}
              >
                {error}
              </p>
            ))}
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #ddd", padding: "10px 16px", justifyContent: "flex-end" }}>
          <Button
            onClick={() => {
              setValidationErrorDialogOpen(false);
              setValidationErrors([]);
            }}
            sx={{ color: "#1976d2", fontSize: "14px", fontWeight: 500 }}
          >
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>

      {props.embedded ? (
        body
      ) : (
        <BootstrapDialog onClose={props.onClose} aria-labelledby="customized-dialog-title" open={props.isOpen}>
          {body}
        </BootstrapDialog>
      )}
    </div>
  );
}

export default LongTermUploadContent;
